import { z } from "zod";

// ── Account type enum ─────────────────────────────────────────────────────────

export const AccountTypeValues = [
  "asset",
  "liability",
  "equity",
  "revenue",
  "expense",
] as const;

export type AccountType = (typeof AccountTypeValues)[number];

export const AccountTypes = AccountTypeValues.map((value) => {
  const labels: Record<AccountType, string> = {
    asset: "Asset",
    liability: "Liability",
    equity: "Equity",
    revenue: "Revenue",
    expense: "Expense",
  };
  return { value, label: labels[value] };
});

// ── Domain schema (DB / server truth) ─────────────────────────────────────────

export const AccountsSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  type: z.enum([...AccountTypeValues] as [AccountType, ...AccountType[]]),
  description: z.string().optional(),
  parentAccountId: z.string().optional(),
  balance: z.number(),
  totalDebits: z.number(),
  totalCredits: z.number(),
  classId: z.string().optional(),
  class: z.string().optional(),
  createdDate: z.union([z.string(), z.date()]),
  lastModified: z.union([z.string(), z.date()]),
});

export type Account = z.infer<typeof AccountsSchema>;

type AccountBalanceLike = Pick<
  Account,
  "type" | "totalDebits" | "totalCredits"
>;

/**
 * Derive signed account balance from totals using normal-side accounting rules.
 * Assets/expenses are debit-normal, liabilities/equity/revenue are credit-normal.
 */
export function computeAccountBalance(account: AccountBalanceLike): number {
  const debits = account.totalDebits ?? 0;
  const credits = account.totalCredits ?? 0;
  const isCreditNormal =
    account.type === "liability" ||
    account.type === "equity" ||
    account.type === "revenue";

  return isCreditNormal ? credits - debits : debits - credits;
}

export interface AccountNode extends Account {
  children?: AccountNode[];
}

export function buildAccountTree(accounts: Account[]): AccountNode[] {
  const map = new Map<string, AccountNode>();
  for (const acc of accounts) {
    map.set(acc.id, { ...acc, children: [] });
  }
  const roots: AccountNode[] = [];
  for (const node of map.values()) {
    if (node.parentAccountId && map.has(node.parentAccountId)) {
      map.get(node.parentAccountId)!.children!.push(node);
    } else {
      roots.push(node);
    }
  }
  for (const node of map.values()) {
    if (node.children?.length === 0) delete node.children;
  }
  rollupSubtreeBalances(roots);
  return roots;
}

/** Post-order traversal: each parent's balance becomes the sum of its own direct
 *  entries plus all descendants. Mutates nodes in-place. */
function rollupSubtreeBalances(nodes: AccountNode[]): void {
  for (const node of nodes) {
    if (node.children?.length) {
      rollupSubtreeBalances(node.children);
      for (const child of node.children) {
        node.balance += child.balance;
        node.totalDebits += child.totalDebits;
        node.totalCredits += child.totalCredits;
      }
    }
  }
}

export const CreateAccountsSchema = AccountsSchema.omit({
  id: true,
  createdDate: true,
  lastModified: true,
  balance: true,
  totalDebits: true,
  totalCredits: true,
});
export const UpdateAccountsSchema = CreateAccountsSchema.partial();

export type CreateAccountsInput = z.infer<typeof CreateAccountsSchema>;
export type UpdateAccountsInput = z.infer<typeof UpdateAccountsSchema>;

// ── Form schema (UI layer) ────────────────────────────────────────────────────

export const AccountsFormSchema = z.object({
  code: z.string(),
  name: z.string().min(1, "Name is required"),
  type: z.enum([...AccountTypeValues] as [AccountType, ...AccountType[]]),
  description: z.string().optional(),
  parentAccountId: z.string().optional(),
  classId: z.string().optional(),
});

export type AccountsForm = z.infer<typeof AccountsFormSchema>;

// ── Mapping: Form → Domain ────────────────────────────────────────────────────

export function formToDomain(input: AccountsForm): UpdateAccountsInput {
  return input;
}

export function domainToForm(item: Account): AccountsForm {
  return {
    code: item.code,
    name: item.name,
    type: item.type,
    description: item.description,
    parentAccountId: item.parentAccountId,
    classId: item.classId,
  };
}
